/**
 * Inline Quickbuy - Shows variant picker directly on product card
 * 
 * This replaces the shadow DOM quickbuy modal with an inline variant picker
 * that works properly with Klaviyo BIS popups.
 */

(function() {
  'use strict';

  // Wait for Klaviyo to be ready
  function waitForKlaviyo(callback) {
    if (window._klOnsite && window._klOnsite.page) {
      callback();
    } else {
      let attempts = 0;
      const maxAttempts = 50;
      const interval = setInterval(() => {
        if (window._klOnsite && window._klOnsite.page) {
          clearInterval(interval);
          callback();
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          console.warn('Klaviyo _klOnsite object not found after multiple attempts.');
          callback(); // Continue anyway
        }
        attempts++;
      }, 100);
    }
  }

  // Update Klaviyo data
  function updateKlaviyoData(productHandle, productId, variantId) {
    if (!window._klOnsite) return;

    // Extract clean handle
    let cleanHandle = productHandle;
    if (productHandle.includes('/products/')) {
      const match = productHandle.match(/\/products\/([^\/\?]+)/);
      cleanHandle = match ? match[1] : productHandle;
    }
    if (cleanHandle.includes('?')) {
      cleanHandle = cleanHandle.split('?')[0];
    }

    console.log('=== Klaviyo BIS Inline Quickbuy Data ===');
    console.log('Product ID:', productId);
    console.log('Variant ID:', variantId);
    console.log('Product Handle:', cleanHandle);
    console.log('=======================================');

    if (!window._klOnsite.product) {
      window._klOnsite.product = {};
    }

    if (productId) {
      window._klOnsite.product.id = productId;
      window._klOnsite.product.productId = productId;
    }

    if (variantId) {
      window._klOnsite.product.variantId = variantId;
      window._klOnsite.product.variant_id = variantId;
    }

    const cleanUrl = `/products/${cleanHandle}`;
    window._klOnsite.product.url = cleanUrl;
    window._klOnsite.product.handle = cleanHandle;
    window._klOnsite.product.productHandle = cleanHandle;

    if (window._klOnsite.page) {
      window._klOnsite.page.url = cleanUrl;
      window._klOnsite.page.pathname = cleanUrl;
    }
  }

  // Check if product has Backinstock_dont_show tag
  async function checkKlaviyoDisabled(productHandle) {
    try {
      const response = await fetch(`${window.Shopify.routes.root}products/${productHandle}`);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const productData = doc.querySelector('[data-product-json]');
      if (productData) {
        const product = JSON.parse(productData.textContent);
        return product.tags && product.tags.includes('Backinstock_dont_show');
      }
    } catch (e) {
      console.error('Error checking Klaviyo disabled:', e);
    }
    return false;
  }

  // Force Klaviyo to use clean product URL (without collection paths)
  function forceCleanKlaviyoURL(productHandle) {
    if (!window._klOnsite || !productHandle) return;

    // Extract clean handle
    let cleanHandle = productHandle;
    if (productHandle.includes('/products/')) {
      const match = productHandle.match(/\/products\/([^\/\?]+)/);
      cleanHandle = match ? match[1] : productHandle;
    }
    if (cleanHandle.includes('?')) {
      cleanHandle = cleanHandle.split('?')[0];
    }

    // Clean URL in Shopify format
    const cleanUrl = `/products/${cleanHandle}`;

    // Patch Klaviyo's page data (in memory only - doesn't affect browser URL)
    if (window._klOnsite.page) {
      window._klOnsite.page.url = cleanUrl;
      window._klOnsite.page.pathname = cleanUrl;
    }

    // BIS specifically checks this
    if (window._klOnsite.product) {
      window._klOnsite.product.url = cleanUrl;
    }
  }

  // Force Klaviyo to re-initialize BIS detection in a container
  function triggerKlaviyoBISDetection(container, productHandle) {
    if (!container || !productHandle) return;
    
    // Ensure clean URL first
    forceCleanKlaviyoURL(productHandle);
    
    // Try to call Klaviyo's internal BIS initialization if available
    if (window._klOnsite) {
      // Note: We don't call initBIS as it's not a valid Klaviyo method
      // Instead, we rely on Klaviyo's automatic detection of the button
    }
    
    // Find all klaviyo-bis-trigger elements
    const triggers = container.querySelectorAll('.klaviyo-bis-trigger');
    
    triggers.forEach(trigger => {
      // Skip if disabled
      if (trigger.hasAttribute('data-klaviyo-disabled')) return;
      
      // Ensure trigger has identifier
      const form = container.querySelector('form[id*="product-form"]');
      if (form && !trigger.hasAttribute('data-a11y-identifier')) {
        const variantId = form.querySelector('input[name="id"]')?.value || Date.now();
        trigger.setAttribute('data-a11y-identifier', `bis-trigger-${variantId}`);
      }
      
      // Temporarily remove and re-add to trigger Klaviyo's MutationObserver
      const parent = trigger.parentNode;
      const nextSibling = trigger.nextSibling;
      
      if (parent) {
        // Remove
        parent.removeChild(trigger);
        
        // Re-add after a brief moment
        requestAnimationFrame(() => {
          if (nextSibling) {
            parent.insertBefore(trigger, nextSibling);
          } else {
            parent.appendChild(trigger);
          }
          
          // Force clean URL again after re-adding
          forceCleanKlaviyoURL(productHandle);
          
          // Also ensure button inside has identifier
          const button = trigger.querySelector('button');
          if (button && !button.hasAttribute('data-a11y-identifier')) {
            const form = container.querySelector('form[id*="product-form"]');
            if (form) {
              const variantId = form.querySelector('input[name="id"]')?.value || Date.now();
              button.setAttribute('data-a11y-identifier', `bis-button-${variantId}`);
            }
          }
        });
      }
    });
  }

  // Trigger Klaviyo BIS
  function triggerKlaviyoBIS(productHandle, productId, variantId) {
    waitForKlaviyo(() => {
      updateKlaviyoData(productHandle, productId, variantId);
      forceCleanKlaviyoURL(productHandle);
      
      if (window._klOnsite && window._klOnsite.openForm) {
        console.log('Triggering Klaviyo BIS popup');
        window._klOnsite.openForm(['XR88SJ']);
      } else {
        console.warn('Klaviyo openForm not available');
      }
    });
  }

  // Initialize Klaviyo BIS for inline quickbuy
  function initKlaviyoBISInline(container, productHandle, productId) {
    if (!container || !productHandle) return;

    // Extract clean handle
    let cleanHandle = productHandle;
    if (productHandle.includes('/products/')) {
      const match = productHandle.match(/\/products\/([^\/\?]+)/);
      cleanHandle = match ? match[1] : productHandle;
    }
    if (cleanHandle.includes('?')) {
      cleanHandle = cleanHandle.split('?')[0];
    }

    // Force clean URL immediately
    forceCleanKlaviyoURL(cleanHandle);

    const form = container.querySelector('form');
    if (!form) return;

    // Find klaviyo-bis-trigger element
    const klaviyoTrigger = container.querySelector('.klaviyo-bis-trigger');
    if (!klaviyoTrigger) return;

    // Skip if Klaviyo is disabled
    if (klaviyoTrigger.hasAttribute('data-klaviyo-disabled')) {
      return;
    }

    const formId = klaviyoTrigger.getAttribute('data-klaviyo-form-id');
    if (!formId) return;

    const button = klaviyoTrigger.querySelector('button.buy-buttons__add-to-cart');
    if (!button) return;

    // Function to update button behavior based on variant availability
    const updateButtonBehavior = () => {
      const currentButton = klaviyoTrigger.querySelector('button.buy-buttons__add-to-cart');
      if (!currentButton) return;

      // Check if Klaviyo is still enabled
      if (klaviyoTrigger.hasAttribute('data-klaviyo-disabled')) {
        if (currentButton.hasAttribute('data-variant-unavailable') || currentButton.disabled) {
          currentButton.type = 'submit';
          currentButton.disabled = true;
        }
        return;
      }

      const isUnavailable = currentButton.hasAttribute('data-variant-unavailable') || currentButton.disabled;

      if (isUnavailable) {
        // Change button to trigger Klaviyo form
        currentButton.type = 'button';
        currentButton.removeAttribute('disabled');

        // Ensure button has data-a11y-identifier for Klaviyo compatibility
        if (!currentButton.hasAttribute('data-a11y-identifier')) {
          const variantId = form.querySelector('input[name="id"]')?.value || Date.now();
          const identifier = `bis-button-${variantId}`;
          currentButton.setAttribute('data-a11y-identifier', identifier);
        }

        // Ensure trigger has identifier
        if (!klaviyoTrigger.hasAttribute('data-a11y-identifier')) {
          const variantId = form.querySelector('input[name="id"]')?.value || Date.now();
          klaviyoTrigger.setAttribute('data-a11y-identifier', `bis-trigger-${variantId}`);
        }

        // Function to handle button click and trigger Klaviyo form
        const handleKlaviyoClick = function(e) {
          e.preventDefault();
          e.stopPropagation();

          // Force clean URL before triggering
          forceCleanKlaviyoURL(cleanHandle);

          // Update Klaviyo data
          const variantId = form.querySelector('input[name="id"]')?.value;
          if (variantId && productId) {
            updateKlaviyoData(cleanHandle, productId, variantId);
          }

          // Trigger Klaviyo form
          if (window._klOnsite && typeof window._klOnsite.openForm === 'function') {
            window._klOnsite.openForm([formId]);
          } else if (window._klOnsite && typeof window._klOnsite.push === 'function') {
            window._klOnsite.push(['openForm', formId]);
          }
        };

        // Remove old click handler by cloning
        const newButton = currentButton.cloneNode(true);
        currentButton.parentNode.replaceChild(newButton, currentButton);

        // Ensure cloned button also has the identifier
        if (!newButton.hasAttribute('data-a11y-identifier')) {
          const variantId = form.querySelector('input[name="id"]')?.value || Date.now();
          const identifier = `bis-button-${variantId}`;
          newButton.setAttribute('data-a11y-identifier', identifier);
        }

        // Add Klaviyo form trigger
        newButton.addEventListener('click', handleKlaviyoClick);
      } else {
        // Restore normal button behavior
        currentButton.type = 'submit';
      }
    };

    // Initial check
    updateButtonBehavior();

    // Listen for variant changes
    form.addEventListener('variant:change', () => {
      setTimeout(() => {
        // Update identifier with new variant ID
        const newVariantId = form.querySelector('input[name="id"]')?.value || Date.now();
        const newIdentifier = `bis-button-${newVariantId}`;
        const currentButton = klaviyoTrigger.querySelector('button.buy-buttons__add-to-cart');
        if (currentButton) {
          currentButton.setAttribute('data-a11y-identifier', newIdentifier);
        }
        klaviyoTrigger.setAttribute('data-a11y-identifier', `bis-trigger-${newVariantId}`);
        
        updateButtonBehavior();
        // Trigger Klaviyo BIS detection after variant change
        triggerKlaviyoBISDetection(container, cleanHandle);
      }, 100);
    });

    // Watch for Klaviyo's native BIS button injection
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            // Check if Klaviyo's native button container was injected
            const klaviyoContainer = node.matches && node.matches('#klaviyo-bis-button-container') 
              ? node 
              : (node.querySelector && node.querySelector('#klaviyo-bis-button-container'));
            
            // Also check for button with bis-button identifier
            const klaviyoNativeButton = node.matches && node.matches('[data-a11y-identifier*="bis-button"]') 
              ? node 
              : (node.querySelector && node.querySelector('[data-a11y-identifier*="bis-button"]'));
            
            if (klaviyoContainer || klaviyoNativeButton) {
              const targetElement = klaviyoContainer || klaviyoNativeButton;
              
              // Ensure clean URL is set for Klaviyo's native button
              if (cleanHandle) {
                forceCleanKlaviyoURL(cleanHandle);
              }
              
              // Find the actual button element
              const nativeButton = targetElement.querySelector('button[data-a11y-identifier*="bis-button"]') || targetElement.querySelector('button');
              
              if (nativeButton && nativeButton.tagName === 'BUTTON') {
                // Ensure the button has the data-a11y-identifier attribute
                if (!nativeButton.hasAttribute('data-a11y-identifier')) {
                  // Generate identifier similar to Klaviyo's format
                  const variantId = form.querySelector('input[name="id"]')?.value || 'default';
                  const identifier = `bis-button-${variantId}`;
                  nativeButton.setAttribute('data-a11y-identifier', identifier);
                  
                  // Also ensure parent container has it if it's the container
                  if (klaviyoContainer && !klaviyoContainer.hasAttribute('data-a11y-identifier')) {
                    klaviyoContainer.setAttribute('data-a11y-identifier', identifier);
                  }
                }
                
                // Add click handler to ensure clean URL
                nativeButton.addEventListener('click', function() {
                  if (cleanHandle) {
                    forceCleanKlaviyoURL(cleanHandle);
                    const variantId = form.querySelector('input[name="id"]')?.value;
                    if (variantId && productId) {
                      updateKlaviyoData(cleanHandle, productId, variantId);
                    }
                  }
                }, { once: false });
              }
            }
          }
        });
      });
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-a11y-identifier']
    });

    // Trigger Klaviyo BIS detection after a delay
    setTimeout(() => {
      triggerKlaviyoBISDetection(container, cleanHandle);
    }, 150);
  }

  // Resolve line item attribution from product card data, with runtime overrides (e.g. checkout upsell mode)
  function resolveAttribution(container) {
    const attributionEl = container.closest('[data-attribution]');
    if (!attributionEl) return null;

    const attribution = attributionEl.getAttribute('data-attribution');
    if (!attribution) return null;

    const cartDrawer = container.closest('cart-drawer');
    if (cartDrawer && attribution === 'cart_drawer_complimentary' && cartDrawer.classList.contains('cart-drawer--checkout-upsell')) {
      return 'cart_drawer_upsell';
    }

    return attribution;
  }

  // Keep hidden _attribution input in sync before product-form submit (single-variant quick add)
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.action?.includes('/cart/add')) return;

    const attribution = resolveAttribution(form);
    if (!attribution) return;

    let input = form.querySelector('input[name="properties[_attribution]"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[_attribution]';
      form.appendChild(input);
    }
    input.value = attribution;
  }, true);

  // Initialize inline quickbuy
  function initInlineQuickbuy(container) {
    const trigger = container.querySelector('[data-inline-quickbuy-trigger]');
    const variantPicker = container.querySelector('[data-inline-variant-picker]');
    const form = container.querySelector('form');
    const variantIdInput = container.querySelector('[data-variant-id-input]');
    const addToCartButton = container.querySelector('[data-inline-add-to-cart]');
    const buttonText = container.querySelector('[data-button-text]');
    const sizeLabels = container.querySelectorAll('[data-size-label]');
    const productHandle = container.getAttribute('data-product-handle');
    const productId = container.getAttribute('data-product-id');

    if (!trigger || !variantPicker || !form) return;

    // Find the product card container and color swatches wrapper
    const productCard = container.closest('.product-card');
    const colorSwatchesWrapper = productCard ? productCard.querySelector('.ProductCard--ColorSwatchesFieldsetWrapper') : null;

    let isOpen = false;

    // Initialize Klaviyo BIS
    initKlaviyoBISInline(container, productHandle, productId);

    // Toggle variant picker visibility
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      isOpen = !isOpen;
      
      if (isOpen) {
        variantPicker.style.display = 'block';
        // Hide color swatches wrapper
        if (colorSwatchesWrapper) {
          colorSwatchesWrapper.style.opacity = '0';
        }
        // Scroll into view if needed
        setTimeout(() => {
          variantPicker.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          // Trigger Klaviyo BIS detection when picker opens
          triggerKlaviyoBISDetection(container, productHandle);
        }, 100);
      } else {
        variantPicker.style.display = 'none';
        // Show color swatches wrapper again
        if (colorSwatchesWrapper) {
          colorSwatchesWrapper.style.opacity = '1';
        }
      }
    });

    // Function to add variant to cart
    async function addVariantToCart(variantId, quantity = 1) {
      if (!variantId) return false;
      
      try {
        // Show loading state
        if (addToCartButton) {
          addToCartButton.disabled = true;
          addToCartButton.setAttribute('aria-busy', 'true');
          if (buttonText) {
            buttonText.textContent = 'Tilføjer...';
          }
        }
        
        // Dispatch loading start event
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
        
        // Prepare sections to bundle (for cart drawer refresh)
        // Cart drawer will listen to this event and add its section ID
        let sectionsToBundle = [];
        const sectionsEvent = new CustomEvent('cart:prepare-bundled-sections', { 
          bubbles: true, 
          detail: { sections: sectionsToBundle },
          cancelable: false
        });
        document.documentElement.dispatchEvent(sectionsEvent);
        
        // Also try to get cart drawer section ID directly
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          // Try to extract section ID from cart drawer's parent section
          const cartDrawerSection = cartDrawer.closest('.shopify-section');
          if (cartDrawerSection && cartDrawerSection.id) {
            const sectionId = cartDrawerSection.id.replace('shopify-section-', '');
            if (sectionId && !sectionsToBundle.includes(sectionId)) {
              sectionsToBundle.push(sectionId);
            }
          }
        }
        
        // Add to cart via Shopify AJAX API (with sections for cart drawer)
        const formData = new FormData();
        formData.set('id', variantId);
        formData.set('quantity', quantity);
        const attribution = resolveAttribution(container);
        if (attribution) {
          formData.set('properties[_attribution]', attribution);
        }
        if (sectionsToBundle.length > 0) {
          formData.set('sections', sectionsToBundle.join(','));
          formData.set('sections_url', `${window.Shopify.routes.root}variants/${variantId}`);
        }
        
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          body: formData,
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        
        const responseJson = await response.json();
        
        // Dispatch loading end event
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        
        if (response.ok) {
          // Get updated cart
          const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
          const cart = await cartResponse.json();
          
          // Add sections to cart object (for cart drawer refresh)
          if (responseJson.sections) {
            cart.sections = responseJson.sections;
          }
          
          // Dispatch variant:add event (similar to theme.js)
          form.dispatchEvent(new CustomEvent('variant:add', {
            bubbles: true,
            detail: {
              items: responseJson.hasOwnProperty('items') ? responseJson.items : [responseJson],
              cart: cart,
              onSuccessDo: 'force_open_drawer'
            }
          }));
          
          // Dispatch cart change event (this triggers cart drawer to open)
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'variant:add',
              onSuccessDo: 'force_open_drawer',
              cart: cart,
              productIdAdded: variantId
            }
          }));
          
          // Reset button state
          if (addToCartButton) {
            addToCartButton.disabled = false;
            addToCartButton.removeAttribute('aria-busy');
            if (buttonText) {
              buttonText.textContent = buttonText.getAttribute('data-add-text') || 'Læg i kurv';
            }
          }
          
          // Close the variant picker
          isOpen = false;
          variantPicker.style.display = 'none';
          
          return true;
        } else {
          // Handle error
          throw new Error(responseJson.description || 'Failed to add to cart');
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
        
        // Reset button state
        if (addToCartButton) {
          addToCartButton.disabled = false;
          addToCartButton.removeAttribute('aria-busy');
          if (buttonText) {
            buttonText.textContent = buttonText.getAttribute('data-add-text') || 'Læg i kurv';
          }
        }
        
        // Dispatch loading end event
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        
        // Dispatch cart error event
        form.dispatchEvent(new CustomEvent('cart:error', {
          bubbles: true,
          detail: {
            error: error.message
          }
        }));
        
        return false;
      }
    }

    // Handle variant selection
    const sizeInputs = container.querySelectorAll('input[type="radio"][name^="size-option"]');
    sizeInputs.forEach(input => {
      // Handle click on size label - immediately add to cart if available, or navigate if sold out
      const label = container.querySelector(`label[for="${input.id}"]`);
      if (label) {
        label.addEventListener('click', async (e) => {
          const variantId = input.getAttribute('data-variant-id');
          const variantAvailable = input.getAttribute('data-variant-available') === 'true';
          
          // Prevent default radio selection behavior temporarily
          e.preventDefault();
          
          // Select the radio input
          input.checked = true;
          
          // Update variant ID input
          if (variantIdInput) {
            variantIdInput.value = variantId;
          }
          
          // Update label styles
          sizeLabels.forEach(lbl => {
            const labelInput = container.querySelector(`#${lbl.getAttribute('for')}`);
            if (labelInput && labelInput.checked) {
              lbl.style.borderColor = '#000000';
              lbl.style.borderWidth = '2px';
              lbl.style.fontWeight = '600';
            } else {
              lbl.style.borderColor = '#e5e5e5';
              lbl.style.borderWidth = '1px';
              lbl.style.fontWeight = 'normal';
            }
          });
          
          // Update Klaviyo data
          updateKlaviyoData(productHandle, productId, variantId);
          
          if (variantAvailable && variantId) {
            // Variant is available - immediately add to cart
            await addVariantToCart(variantId);
          } else if (!variantAvailable && variantId) {
            // Variant is sold out - navigate to product page
            setTimeout(async () => {
              // Check if Klaviyo should be disabled
              const klaviyoDisabled = await checkKlaviyoDisabled(productHandle);
              
              if (klaviyoDisabled) {
                // Just navigate without triggering BIS
                const productUrl = `${window.Shopify.routes.root}products/${productHandle}?variant=${variantId}`;
                window.location.href = productUrl;
                return;
              }
              
              // Navigate to product page with variant and trigger BIS parameter
              const productUrl = `${window.Shopify.routes.root}products/${productHandle}?variant=${variantId}&triggerBIS=true`;
              window.location.href = productUrl;
            }, 50);
          }
        });
      }
      
      // Keep change event for updating button state (though button may be hidden)
      input.addEventListener('change', () => {
        const variantId = input.getAttribute('data-variant-id');
        const variantAvailable = input.getAttribute('data-variant-available') === 'true';
        
        if (variantIdInput) {
          variantIdInput.value = variantId;
        }

        // Update button state (for fallback/display purposes)
        if (addToCartButton && buttonText) {
          if (variantAvailable) {
            addToCartButton.disabled = false;
            addToCartButton.removeAttribute('data-variant-unavailable');
            buttonText.textContent = buttonText.getAttribute('data-add-text') || 'Læg i kurv';
          } else {
            addToCartButton.setAttribute('data-variant-unavailable', 'true');
            buttonText.textContent = buttonText.getAttribute('data-sold-out-text') || 'Udsolgt';
          }
        }

        // Update Klaviyo data
        updateKlaviyoData(productHandle, productId, variantId);
        
        // Trigger Klaviyo BIS detection after variant change
        setTimeout(() => {
          triggerKlaviyoBISDetection(container, productHandle);
        }, 100);
      });
    });

    // Handle form submission - Klaviyo button will handle unavailable variants
    // This is just a fallback in case Klaviyo button doesn't exist
    form.addEventListener('submit', async (e) => {
      const currentVariantId = variantIdInput ? variantIdInput.value : null;
      
      if (!currentVariantId) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      const currentInput = container.querySelector(`input[data-variant-id="${currentVariantId}"]:checked`);
      const variantAvailable = currentInput ? currentInput.getAttribute('data-variant-available') === 'true' : false;

      // Check if Klaviyo native button exists - if so, let it handle it
      const klaviyoNativeButton = container.querySelector('#klaviyo-bis-button-container button, [data-a11y-identifier*="bis-button"]');
      if (klaviyoNativeButton && !variantAvailable) {
        // Klaviyo button exists, let it handle the click
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (!variantAvailable) {
        e.preventDefault();
        e.stopPropagation();

        // Check if Klaviyo should be disabled
        const klaviyoDisabled = await checkKlaviyoDisabled(productHandle);
        
        if (klaviyoDisabled) {
          // Just show sold out message - don't trigger Klaviyo
          if (buttonText) {
            buttonText.textContent = 'Udsolgt';
            setTimeout(() => {
              buttonText.textContent = buttonText.getAttribute('data-sold-out-text') || 'Udsolgt';
            }, 2000);
          }
          return;
        }

        // Fallback: Trigger Klaviyo BIS if native button doesn't exist
        triggerKlaviyoBIS(productHandle, productId, currentVariantId);
      }
      // If variant is available, let form submit normally
    });

    // Close picker when clicking outside
    document.addEventListener('click', (e) => {
      if (isOpen && !container.contains(e.target)) {
        isOpen = false;
        variantPicker.style.display = 'none';
        // Show color swatches wrapper again
        if (colorSwatchesWrapper) {
          colorSwatchesWrapper.style.opacity = '1';
        }
      }
    });

    // Initialize button text attributes
    if (buttonText) {
      const addText = buttonText.textContent.trim();
      buttonText.setAttribute('data-add-text', addText);
      // Try to get sold out text from buy-buttons if available, otherwise use default
      const soldOutText = buttonText.getAttribute('data-sold-out-text') || 'Udsolgt';
      buttonText.setAttribute('data-sold-out-text', soldOutText);
    }

    // Set initial state - prefer first available variant
    let checkedInput = container.querySelector('input[type="radio"]:checked');
    if (!checkedInput || checkedInput.getAttribute('data-variant-available') !== 'true') {
      // Find first available variant
      const availableInputs = Array.from(sizeInputs).filter(input => 
        input.getAttribute('data-variant-available') === 'true'
      );
      if (availableInputs.length > 0) {
        checkedInput = availableInputs[0];
        checkedInput.checked = true;
      }
    }
    
    if (checkedInput) {
      checkedInput.dispatchEvent(new Event('change'));
    }
  }

  // Initialize all inline quickbuy containers
  function init() {
    const containers = document.querySelectorAll('.product-card__inline-quickbuy');
    containers.forEach(container => {
      initInlineQuickbuy(container);
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Watch for dynamically added containers
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          if (node.classList && node.classList.contains('product-card__inline-quickbuy')) {
            initInlineQuickbuy(node);
          } else if (node.querySelectorAll) {
            const containers = node.querySelectorAll('.product-card__inline-quickbuy');
            containers.forEach(container => {
              initInlineQuickbuy(container);
            });
          }
        }
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
})();

